import { LoaderCircle, X } from 'lucide-react';
import {
  useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import type { Tone } from '../lib/labels';

export const cn = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

type Kind = 'primary' | 'secondary' | 'ghost' | 'danger';
const KIND: Record<Kind, string> = {
  primary: 'bg-primary text-on-primary border-primary hover:opacity-90',
  secondary: 'bg-surface text-text border-border hover:bg-surface-2',
  ghost: 'bg-transparent text-link border-transparent hover:bg-accent-soft',
  danger: 'bg-surface text-critical border-critical hover:bg-critical-bg',
};

export function Button({ kind = 'primary', loading, icon, children, className, disabled, ...rest }:
  ButtonHTMLAttributes<HTMLButtonElement> & { kind?: Kind; loading?: boolean; icon?: ReactNode }) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-2 min-h-10 px-4 rounded-xl border-[1.5px] font-semibold text-sm transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer',
        KIND[kind], className,
      )}>
      {loading ? <LoaderCircle className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

const fieldBox = 'w-full rounded-xl border-[1.5px] border-border bg-surface px-3 py-2.5 text-sm text-text placeholder:text-muted focus:border-link focus:outline-none';

export function Field({ label, error, hint, children, htmlFor }: { label: string; error?: string; hint?: string; children: ReactNode; htmlFor: string }) {
  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <label htmlFor={htmlFor} className="text-sm font-semibold">{label}</label>
      {children}
      {error ? <p className="text-sm text-critical" role="alert">{error}</p> : hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}

export function Input({ label, error, hint, id, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string; hint?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return <Field label={label} error={error} hint={hint} htmlFor={fid}><input id={fid} {...rest} className={fieldBox} aria-invalid={!!error} /></Field>;
}

export function Select({ label, error, id, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; error?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return <Field label={label} error={error} htmlFor={fid}><select id={fid} {...rest} className={fieldBox}>{children}</select></Field>;
}

export function Textarea({ label, error, hint, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; error?: string; hint?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  return <Field label={label} error={error} hint={hint} htmlFor={fid}><textarea id={fid} rows={3} {...rest} className={cn(fieldBox, 'resize-y')} /></Field>;
}

const TONE: Record<Tone, string> = {
  ok: 'bg-ok-bg text-ok', warn: 'bg-warn-bg text-warn', alert: 'bg-alert-bg text-alert',
  critical: 'bg-critical-bg text-critical', info: 'bg-info-bg text-info', neutral: 'bg-neutral-bg text-neutral',
};

/** Estado siempre con texto (y opcionalmente ícono): nunca solo color. */
export function Badge({ tone, children, icon }: { tone: Tone; children: ReactNode; icon?: ReactNode }) {
  return <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap', TONE[tone])}>{icon}{children}</span>;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <section className={cn('rounded-2xl border border-border bg-surface p-5', className)}>{children}</section>;
}

export function Spinner({ label = 'Cargando' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center p-10 text-muted" role="status">
      <LoaderCircle className="size-6 animate-spin" aria-hidden /><span className="sr-only">{label}</span>
    </div>
  );
}

export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl bg-critical-bg p-4 text-critical">
      <span className="flex-1">{message}</span>
      {onRetry ? <Button kind="secondary" onClick={onRetry}>Reintentar</Button> : null}
    </div>
  );
}

/** Diálogo modal accesible (foco atrapado por <dialog>, Esc para cerrar). */
export function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} aria-labelledby="modal-title"
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-2xl border border-border bg-surface p-0 text-text backdrop:bg-black/40">
      <div className="flex items-center justify-between gap-4 border-b border-border px-5 py-4">
        <h2 id="modal-title" className="text-lg font-extrabold">{title}</h2>
        <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-lg p-1.5 hover:bg-surface-2 cursor-pointer"><X className="size-5" /></button>
      </div>
      <div className="flex flex-col gap-4 p-5">{children}</div>
    </dialog>
  );
}

export function EmptyState({ title, text, icon }: { title: string; text?: string; icon?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 p-10 text-center">
      {icon ? <div className="rounded-2xl bg-accent-soft p-3 text-link">{icon}</div> : null}
      <p className="font-bold">{title}</p>
      {text ? <p className="max-w-prose text-sm text-muted">{text}</p> : null}
    </div>
  );
}
